"use client";

import { useEffect, useMemo, useState } from "react";
import API from "../utils/api";
import "./ReturnReport.css";
import { format } from "date-fns";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const ReturnReport = () => {
  const [activeTab, setActiveTab] = useState("today");
  const [fromDate, setFromDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [toDate, setToDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [generatingCSV, setGeneratingCSV] = useState(false);
  const [groupedRows, setGroupedRows] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [enrichedItems, setEnrichedItems] = useState([]);
  const [enriching, setEnriching] = useState(false);
  // Products cache used to enrich old return items missing size
  const [allProducts, setAllProducts] = useState([]);

  // Extract a usable product id from various backend shapes
  const getProductId = (item) => {
    if (!item) return null;
    if (item.productId) return item.productId;
    // Some APIs send `product` as an ObjectId string
    if (typeof item.product === 'string') {
      const s = item.product.trim();
      if (/^[0-9a-fA-F]{24}$/.test(s)) return s;
    }
    // Or as an embedded object with _id
    if (item.product && typeof item.product === 'object' && item.product._id) {
      return item.product._id;
    }
    return null;
  };

  useEffect(() => {
    if (activeTab === 'today') {
      const today = format(new Date(), 'yyyy-MM-dd')
      fetchByRange(today, today)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  // Load products once for client-side enrichment of old records
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const res = await API.get("/products")
        setAllProducts(Array.isArray(res.data) ? res.data : [])
      } catch (_) {
        setAllProducts([])
      }
    }
    loadProducts()
  }, [])

  const groupByDateAndCustomer = (rows = []) => {
    const groupsMap = new Map();
    for (const r of rows) {
      const dateStr = r.date ? format(new Date(r.date), "yyyy-MM-dd") : "N/A";
      const key = `${dateStr}::${r.customerName || "Unknown"}`;
      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          key,
          date: dateStr,
          customerName: r.customerName || "Unknown",
          items: [],
          totalQuantity: 0,
        });
      }
      const g = groupsMap.get(key);
      g.items.push(r);
      g.totalQuantity += Number(r.quantity) || 0;
    }
    return Array.from(groupsMap.values());
  };

  const fetchByRange = async (start, end) => {
    setLoading(true);
    try {
      const res = await API.get(`/returns/report?start=${start}&end=${end}`);
      const items = (res.data?.returns || res.data || []).map((it) => ({
        ...it,
        // Normalize product id for later enrichment
        productId: it.productId || (typeof it.product === 'string' ? it.product : it.product?._id),
        // Ensure size is present from nested product when backend omitted it
        size: it.size || it.product?.size || it.productSize || '-',
      }));
      setGroupedRows(groupByDateAndCustomer(items));
    } catch (err) {
      console.error("Error fetching returns:", err);
      setMessage("❌ Failed to fetch return report");
      setGroupedRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Index products by id and by (companyName::modelNo) for quick lookups
  const productById = useMemo(() => {
    const map = new Map()
    for (const p of allProducts || []) {
      if (p && p._id) map.set(p._id, p)
    }
    return map
  }, [allProducts])

  const normalize = (s) => (typeof s === 'string' ? s.trim().toLowerCase() : '')
  const makeKey = (companyName, modelNo) => `${normalize(companyName)}::${normalize(modelNo)}`

  const productByKey = useMemo(() => {
    const map = new Map()
    for (const p of allProducts || []) {
      const key = makeKey(p?.companyName, p?.modelNo)
      if (key && !map.has(key)) {
        map.set(key, p)
      }
    }
    return map
  }, [allProducts])

  // When opening details, ensure size is populated for old records too
  useEffect(() => {
    const run = async () => {
      if (!selectedGroup) {
        setEnrichedItems([])
        return
      }
      const items = Array.isArray(selectedGroup.items) ? selectedGroup.items : []

      // 1) Try local enrichment from loaded products (by id, or by company+model)
      const locallyFilled = items.map((it) => {
        const currentSize = it.size || it.product?.size || it.productSize
        if (currentSize && currentSize !== '-') return it

        const pid = getProductId(it)
        if (pid && productById.has(pid)) {
          const p = productById.get(pid)
          const sz = p?.size
          if (sz) {
            return { ...it, companyName: it.companyName || p.companyName, modelNo: it.modelNo || p.modelNo, size: sz }
          }
        }

        const key = makeKey(it.companyName, it.modelNo)
        if (key && productByKey.has(key)) {
          const p = productByKey.get(key)
          const sz = p?.size
          if (sz) {
            return { ...it, companyName: it.companyName || p.companyName, modelNo: it.modelNo || p.modelNo, size: sz }
          }
        }
        return it
      })

      // Determine which still need network enrichment
      const needsNetwork = locallyFilled.filter((it) => {
        const hasSize = it.size && it.size !== '-'
        const pid = getProductId(it)
        return !hasSize && !!pid
      })

      if (needsNetwork.length === 0) {
        setEnrichedItems(locallyFilled)
        return
      }

      setEnriching(true)
      try {
        const uniqueIds = Array.from(new Set(needsNetwork.map((m) => getProductId(m)).filter(Boolean)))
        const detailPromises = uniqueIds.map((id) =>
          API.get(`/products/${id}/details`).then((res) => ({ id, data: res.data })).catch(() => ({ id, data: null }))
        )
        const details = await Promise.all(detailPromises)
        const byId = new Map(details.map(({ id, data }) => [id, data]))
        const filled = locallyFilled.map((it) => {
          if (it.size && it.size !== '-') return it
          const pid = getProductId(it)
          if (!pid) return it
          const d = byId.get(pid)
          if (!d) return it
          return {
            ...it,
            companyName: it.companyName || d.companyName,
            modelNo: it.modelNo || d.modelNo,
            size: d.size || it.size || '-',
          }
        })
        setEnrichedItems(filled)
      } catch (_) {
        setEnrichedItems(locallyFilled)
      } finally {
        setEnriching(false)
      }
    }
    run()
  }, [selectedGroup, productById, productByKey])

  const downloadPDF = () => {
    setGeneratingPDF(true);
    
    try {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Return Report", 105, 15, { align: "center" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Generated on: ${format(new Date(), "yyyy-MM-dd HH:mm")}`, 105, 22, { align: "center" });

      const tableData = (groupedRows.length > 0 ? groupedRows : []).map((g) => [
        g.date,
        g.customerName,
        g.totalQuantity.toString(),
      ]);

      autoTable(doc, {
        head: [['Date', 'Customer', 'Quantity']],
        body: tableData,
        startY: 25,
        margin: { top: 25 },
        styles: {
          fontSize: 9,
          cellPadding: 3,
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [22, 160, 133],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: 45 },
          1: { cellWidth: 80 },
          2: { cellWidth: 40, halign: 'right' },
        }
      });

      doc.save(`return-report-${format(new Date(), "yyyy-MM-dd")}.pdf`);
      setMessage("✅ PDF downloaded successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
      setMessage("❌ Failed to generate PDF");
    } finally {
      setGeneratingPDF(false);
    }
  };

  const downloadCSV = () => {
    if (!groupedRows || groupedRows.length === 0) return;
    setGeneratingCSV(true);
    try {
      const header = ["Date", "Customer", "Quantity"];
      const rows = groupedRows.map((g) => [g.date, g.customerName, g.totalQuantity]);
      const csv = [header, ...rows]
        .map((r) => r.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `return-report-${format(new Date(), "yyyy-MM-dd")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setMessage("✅ CSV downloaded successfully");
    } catch (e) {
      setMessage("❌ Failed to generate CSV");
    } finally {
      setGeneratingCSV(false);
    }
  };

  return (
    <div className="return-report-container">
      <div className="report-header">
        <h2>
          <i className="fas fa-undo-alt"></i> Return Report
        </h2>
        <div className="download-actions">
          <button 
            onClick={downloadPDF} 
            disabled={generatingPDF || groupedRows.length === 0}
            className="download-pdf-btn"
          >
            {generatingPDF ? (
              <><i className="fas fa-spinner fa-spin"></i> Generating PDF...</>
            ) : (
              <><i className="fas fa-download"></i> Download PDF</>
            )}
          </button>
          <button 
            onClick={downloadCSV} 
            disabled={generatingCSV || groupedRows.length === 0}
            className="download-csv-btn"
          >
            {generatingCSV ? (
              <><i className="fas fa-spinner fa-spin"></i> Generating...</>
            ) : (
              <><i className="fas fa-file-excel"></i> Download Excel</>
            )}
          </button>
        </div>
      </div>

      <div className="report-controls">
        <div className="tabs">
          <button className={`tab-btn ${activeTab === 'today' ? 'active' : ''}`} onClick={() => setActiveTab('today')}>Today</button>
          <button className={`tab-btn ${activeTab === 'range' ? 'active' : ''}`} onClick={() => setActiveTab('range')}>From–To</button>
        </div>
        <div className="range-controls">
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <span className="separator">to</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          <button className="apply-btn" onClick={() => fetchByRange(fromDate, toDate)}>Apply</button>
        </div>
      </div>

      {loading ? (
        <p>Loading report...</p>
      ) : groupedRows.length === 0 ? (
        <p>No return records found.</p>
      ) : selectedGroup ? (
        <div className="report-details">
          <button className="back-btn" onClick={() => setSelectedGroup(null)}>← Back to Returns</button>
          <h3>Customer: {selectedGroup.customerName} — {selectedGroup.date}</h3>
          {enriching && <p>Loading details...</p>}
          <table className="report-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Model</th>
                <th>Size</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {(enrichedItems.length > 0 ? enrichedItems : selectedGroup.items).map((item, idx) => (
                <tr key={idx}>
                  <td>{item.companyName}</td>
                  <td>{item.modelNo}</td>
                  <td>{item.size || item.product?.size || item.productSize || '-'}</td>
                  <td>{item.quantity}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>Total</td>
                <td>{(enrichedItems.length > 0 ? enrichedItems : selectedGroup.items).reduce((sum, r) => sum + (Number(r.quantity) || 0), 0)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <table className="report-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Customer</th>
              <th>Quantity</th>
            </tr>
          </thead>
          <tbody>
            {groupedRows.map((row) => (
              <tr key={row.key} className="clickable-row" onClick={() => setSelectedGroup(row)}>
                <td>{row.date}</td>
                <td>{row.customerName}</td>
                <td>{row.totalQuantity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {message && <p className="report-message">{message}</p>}
    </div>
  );
};

export default ReturnReport;

